import { ReactNode } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import {
  Content,
  ContentHeader,
  LinkButton,
  SupportButton,
} from '@backstage/core-components';
import {
  coreExtensionData,
  FrontendPlugin,
  useRouteRef,
} from '@backstage/frontend-plugin-api';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityListProvider,
  EntityOwnerPicker,
  EntitySearchBar,
  EntityTagPicker,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import {
  TemplateCategoryPicker,
  TemplateGroups,
} from '@backstage/plugin-scaffolder-react/alpha';

// Legacy `hidden-template` tag (sp-sde-portal used it with `templateFilter`).
const HIDDEN_TEMPLATE_TAG = 'hidden-template';

const isNotHiddenTemplate = (entity: { metadata: { tags?: string[] } }) =>
  !entity.metadata.tags?.includes(HIDDEN_TEMPLATE_TAG);

/**
 * Re-implements the scaffolder template list, adding a `templateFilter` that
 * drops templates tagged `hidden-template`. Mirrors the built-in list
 * (`TemplatesSubPage`) using public building blocks.
 */
const FilteredTemplateList = () => {
  const navigate = useNavigate();
  const getTemplatePath = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
  const getRegisterComponentPath = useRouteRef(
    scaffolderPlugin.externalRoutes.registerComponent,
  );
  const registerComponentLink = getRegisterComponentPath?.();

  return (
    <EntityListProvider>
      <Content>
        <ContentHeader title="">
          {registerComponentLink && (
            <LinkButton
              to={registerComponentLink}
              color="primary"
              variant="outlined"
            >
              Register Existing Component
            </LinkButton>
          )}
          <SupportButton>
            Create new software components using standard templates in your
            organization.
          </SupportButton>
        </ContentHeader>
        <CatalogFilterLayout>
          <CatalogFilterLayout.Filters>
            <EntitySearchBar />
            <EntityKindPicker initialFilter="template" hidden />
            <UserListPicker
              initialFilter="all"
              availableFilters={['all', 'starred']}
            />
            <TemplateCategoryPicker />
            <EntityTagPicker />
            <EntityOwnerPicker />
          </CatalogFilterLayout.Filters>
          <CatalogFilterLayout.Content>
            <TemplateGroups
              groups={[{ title: 'Templates', filter: () => true }]}
              templateFilter={isNotHiddenTemplate}
              onTemplateSelected={template => {
                const { namespace, name } = parseEntityRef(
                  stringifyEntityRef(template),
                );
                const path = getTemplatePath?.({
                  namespace,
                  templateName: name,
                });
                if (path) {
                  navigate(path);
                }
              }}
            />
          </CatalogFilterLayout.Content>
        </CatalogFilterLayout>
      </Content>
    </EntityListProvider>
  );
};

/**
 * Renders the filtered list for the index route and delegates every deeper
 * route (i.e. the create wizard) to Backstage's original sub-page element,
 * leaving the KaaP create/edit wizard flow untouched.
 */
const TemplatesSubPageWithHiddenFilter = ({
  children,
}: {
  children: ReactNode;
}) => (
  <Routes>
    <Route index element={<FilteredTemplateList />} />
    <Route path="*" element={<>{children}</>} />
  </Routes>
);

/**
 * The scaffolder plugin with its `templates` sub-page overridden so the
 * template list honors the `hidden-template` tag — the new frontend system
 * exposes no `templateFilter` config, so we wrap the built-in element rather
 * than reproduce the wizard.
 */
export const scaffolderPluginWithHiddenTemplates: FrontendPlugin =
  scaffolderPlugin.withOverrides({
    extensions: [
      scaffolderPlugin
        .getExtension('sub-page:scaffolder/templates')
        .override({
          factory(originalFactory) {
            return [...originalFactory()].map(output =>
              output.id === coreExtensionData.reactElement.id
                ? coreExtensionData.reactElement(
                    <TemplatesSubPageWithHiddenFilter>
                      {output.value as ReactNode}
                    </TemplatesSubPageWithHiddenFilter>,
                  )
                : output,
            );
          },
        }),
    ],
  },
);
